import { call, select } from 'redux-saga/effects';

import type {
  ReaderTaskScoringFormCodeTypes,
  ReaderTaskType,
} from '@dynamika-shared/types/reader';

import { selectIsReaderCacheActive } from './readerCache.slice';
import type { ClinicalTrialInventoryState } from '../clinicalTrialInventory/clinicalTrialInventory.types';

/**
 * 🚧 readerCache gate — who is allowed to reuse a cached reading task.
 *
 * Caching the reading task makes visit-switching inside the OHIF viewer fast.
 * Plain scoring forms (DXA & co.) render without the viewer and re-read the task
 * after every start/save/submit, so a cached copy shows them a stale status or
 * draft. Two independent checks keep them out:
 *
 *   1. route  — cache only on `…/viewer` and `…/viewer-detached` (known before
 *               the first fetch, so it gates reads as well as writes)
 *   2. form   — never store a reading whose scoring form is configured as
 *               `viewerEmbedded: false` in the trial inventory
 *
 * Trial-level caches (context, inventory, DICOM resource) stay ungated: they hold
 * configuration, are keyed per trial and dropped on trial switch. Invalidation
 * (`flushParentTask`, `flushTrial`, `flushAll`) is never gated either — dropping
 * stale data must work from any route.
 */

// Matches only the reading-viewer suffixes. `viewer-simple` and `qc-viewer` are
// different pages and must not enable the cache, so the suffix is anchored.
const VIEWER_PATHNAME = /\/viewer(-detached)?\/?$/;

export const isViewerCachePathname = (pathname: string): boolean =>
  VIEWER_PATHNAME.test(pathname);

export const isViewerCacheRoute = (): boolean =>
  isViewerCachePathname(window.location.pathname);

/**
 * Slice presence AND viewer route. Called as `yield call(isReaderCacheEnabled)`
 * so a worker gets one boolean for both its read and its write branch.
 */
export function* isReaderCacheEnabled(): Generator<unknown, boolean, boolean> {
  const isActive: boolean = yield select(selectIsReaderCacheActive);

  if (!isActive) {
    return false;
  }

  return yield call(isViewerCacheRoute);
}

type InventoryRoot = {
  clinicalTrialInventory?: Partial<
    Pick<ClinicalTrialInventoryState, 'scoringForms'>
  >;
};

/**
 * The reading's own scoring form code: the visit the task points at, falling
 * back to the first visit (parent tasks carry the visit list, not a code).
 */
export const resolveTaskScoringCode = (
  task?: Partial<ReaderTaskType> | null
): ReaderTaskScoringFormCodeTypes | undefined => {
  const visits = task?.visits ?? [];
  const currentVisit =
    visits.find(visit => visit.visitId === task?.visitId) ?? visits[0];

  return currentVisit?.scoringCode;
};

/**
 * Blocks caching only when the inventory positively says the form has no
 * embedded viewer. An unknown code (inventory still in flight — task, context
 * and inventory are fetched in parallel) stays cacheable, because the route
 * check has already excluded the plain forms.
 */
export const selectIsCacheableScoringForm = (
  state: InventoryRoot,
  scoringCode?: ReaderTaskScoringFormCodeTypes
): boolean => {
  if (!scoringCode) {
    return true;
  }

  const scoringForm = state.clinicalTrialInventory?.scoringForms?.find(
    ({ code }) => code === scoringCode
  );

  // Form not in the inventory yet (it is fetched in parallel with the task):
  // stay cacheable, the route gate has already excluded the plain forms.
  if (!scoringForm) {
    return true;
  }

  return scoringForm.viewerEmbedded;
};
